/**
 * The model registry (30.03.06): the one way client code reads model config.
 *
 *   getModel('listViewData', type)   one entry
 *   getModel('sidebarData')          the whole map
 *
 * It returns the org's resolved model — base merged with the org's overlays —
 * and the base when there is none. **An uncustomized org gets the base objects
 * themselves, by reference**: `resolved` is null and the lookup is the same
 * object the import used to give (30.03.00 invariant 1).
 *
 * A function, not a hook (30.03.06 §12 q1). Two consumers are plain modules,
 * and the model must change only on the next navigation (§6) — a hook would
 * re-render every consumer the moment a deploy landed, remounting open forms.
 *
 * Call it at render time, never at module scope: a value captured at import
 * cannot see an overlay that arrives later. `npm run lint:modelAccess` fails
 * on both a module-scope call and a direct import of a model map.
 *
 * Translations are not a map here: they load lazily per file, so the org's
 * txt overlays are held by file key (`app/crm/en-US`) and applied as
 * overrides once a page's files are merged (`getTxtOverrides`).
 */

import * as components from './components/index.js'
import { getFastNavItems } from './components/fastNavData.js'
import { extractionData } from './components/extractionData.js'
import { lineDocumentsData } from './components/lineDocumentsData.js'
import viewRoutes from './routes/viewRoutes.js'

// Every map a consumer reads. Most come through components/index.js; the rest
// are named here. `npm run lint:modelAccess` fails on a getModel() of any name
// not in this object — a missing map returns undefined and blanks a page.
const BASE = { ...components, extractionData, lineDocumentsData, viewRoutes }

let resolved = null      // { [mapName]: resolvedMap } — only the maps the org changed
let txt = null           // { 'app/crm/en-US': { mode, patch: { key: string } } }
let version = null       // the modelVersion `resolved` came from; null = base
const listeners = new Set()

const warnedMaps = new Set()
export function getModel(mapName, key) {
    const map = resolved?.[mapName] ?? BASE[mapName]
    if (map === undefined && import.meta.env?.DEV && !warnedMaps.has(mapName)) {
        warnedMaps.add(mapName)
        console.error(`[model] getModel('${mapName}'): no such model map — add it to BASE in model/registry.js`)
    }
    return key === undefined ? map : map?.[key]
}

/**
 * The fast-nav entries of the org's sidebar, derived once per model version:
 * a customer who hides a sidebar entry must not still find it in fast-nav.
 */
let fastNav = { version: undefined, items: [] }
export function getFastNav() {
    if (fastNav.version !== version || fastNav.items.length === 0) {
        fastNav = { version, items: getFastNavItems(getModel('sidebarData')) }
    }
    return fastNav.items
}

/** The base map, whatever the org has changed — for the merge and the authoring UI. */
export const getBaseModel = (mapName) => BASE[mapName]

/**
 * The org's text overrides for one language: every key its txt overlays set,
 * from every file of that language, as one flat object. Applied LAST, after a
 * page's translation files are merged (useTranslation), because the same key
 * can live in two files — `crm.menu.campaign` is in both `app/crm` and
 * `all/shared`, and shared is spread last — so an override of one file alone
 * would lose to the other. `null` in a patch removes the org's override and
 * leaves the platform's string.
 */
const overridesCache = new Map()
export function getTxtOverrides(lang) {
    if (!txt) return null
    const cached = overridesCache.get(lang)
    if (cached && cached.version === version) return cached.values
    const values = {}
    for (const [key, entry] of Object.entries(txt)) {
        if (!key.endsWith(`/${lang}`)) continue
        for (const [k, v] of Object.entries(entry?.patch ?? {})) {
            if (typeof v === 'string') values[k] = v
        }
    }
    overridesCache.set(lang, { version, values })
    return values
}

export const getModelVersion = () => version

export function setResolvedModel({ maps = null, txt: files = null, version: v = null }) {
    resolved = maps && Object.keys(maps).length ? maps : null
    txt = files && Object.keys(files).length ? files : null
    version = v
    if (import.meta.env?.DEV) {
        console.info(`[model] ${version ? `version ${version}: ${Object.keys(resolved ?? {}).length} map(s), ${Object.keys(txt ?? {}).length} txt file(s)` : 'base model'}`)
    }
    for (const fn of listeners) fn(version)
}

// In development, the live registry from the console: `__model.getModel('listViewData', 'contactList')`.
if (import.meta.env?.DEV && typeof window !== 'undefined') {
    window.__model = { getModel, getModelVersion, getTxtOverrides }
}

export const clearResolvedModel = () => setResolvedModel({})

/** Called with the new version whenever the registry changes. Returns an unsubscribe. */
export function subscribeModel(fn) {
    listeners.add(fn)
    return () => listeners.delete(fn)
}
